import React, { useState, useEffect } from 'react';
import { checkUsageLimits, getCurrentMonth, PLAN_LIMITS } from '../utils/usageTracker';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { db as firebaseDb } from '../firebase.config';

const UsageMonitor = ({ companyId, showHeader = true, refreshTrigger = 0 }) => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch usage data
  const fetchUsageData = async () => {
    if (!companyId) {
      setLoading(false);
      setError(null);
      // Show fallback data instead of error when no company selected
      setUsage({
        operations: {
          current: 0,
          limit: 10000,
          percentage: 0,
          exceeded: false,
          remaining: 10000
        },
        storage: {
          current: 0,
          limit: 300,
          percentage: 0,
          exceeded: false,
          remaining: 300
        },
        plan: {
          name: 'Basic',
          price: 49,
          currentPlan: 'basic'
        }
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const usageData = await checkUsageLimits(firebaseDb, companyId);
      
      if (usageData) {
        setUsage(usageData);
      } else {
        // If no usage data, create default structure
        setUsage({
          operations: {
            current: 0,
            limit: 10000,
            percentage: 0,
            exceeded: false,
            remaining: 10000
          },
          storage: {
            current: 0,
            limit: 300,
            percentage: 0,
            exceeded: false,
            remaining: 300
          },
          plan: {
            name: 'Basic',
            price: 49,
            currentPlan: 'basic'
          }
        });
      }
    } catch (err) {
      console.error('Error fetching usage data:', err);
      setError('Failed to load usage data: ' + err.message);
      // Show fallback data on error too
      setUsage({
        operations: {
          current: 0,
          limit: 10000,
          percentage: 0,
          exceeded: false,
          remaining: 10000
        },
        storage: {
          current: 0,
          limit: 300,
          percentage: 0,
          exceeded: false,
          remaining: 300
        },
        plan: {
          name: 'Basic',
          price: 49,
          currentPlan: 'basic'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial load only - no auto-refresh (handled by backend now)
  useEffect(() => {
    fetchUsageData();
    // Backend Cloud Functions now handle automatic tracking and alerts
    // Manual refresh available via refresh button
  }, [companyId, refreshTrigger]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading usage data...</span>
      </div>
    );
  }

  // Error state
  if (error && !usage) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="ml-3 text-sm text-red-700">{error}</p>
        </div>
        <button
          onClick={fetchUsageData}
          className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  // No data - show demo mode for no company selected
  if (!usage) {
    if (!companyId) {
      // Show demo mode when no company selected
      const demoUsage = {
        operations: {
          current: 2500,
          limit: 10000,
          percentage: 25,
          exceeded: false,
          remaining: 7500
        },
        storage: {
          current: 75,
          limit: 300,
          percentage: 25,
          exceeded: false,
          remaining: 225
        },
        plan: {
          name: 'Basic',
          price: 49,
          currentPlan: 'basic'
        }
      };

      return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          {showHeader && (
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800">⚡ Firebase Operations Usage</h3>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">Demo Mode</span>
            </div>
          )}

          {/* Demo Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-blue-800 font-medium">Demo Mode</p>
                <p className="text-blue-700 text-sm mt-1">This is a preview of Firebase operations tracking. Select a company to see real usage data.</p>
              </div>
            </div>
          </div>

          {/* Demo Usage Display */}
          <div className="space-y-6">
            {/* Operations Usage */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Firebase Operations</span>
                <span className="text-sm text-gray-600">{demoUsage.operations.current.toLocaleString()} / {demoUsage.operations.limit.toLocaleString()}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full" 
                  style={{ width: `${Math.min(demoUsage.operations.percentage, 100)}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">{demoUsage.operations.percentage}% used</span>
                <span className="text-xs text-gray-500">{demoUsage.operations.remaining.toLocaleString()} remaining</span>
              </div>
            </div>

            {/* Storage Usage */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Storage Usage</span>
                <span className="text-sm text-gray-600">{demoUsage.storage.current} MB / {demoUsage.storage.limit} MB</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-green-600 h-2.5 rounded-full" 
                  style={{ width: `${Math.min(demoUsage.storage.percentage, 100)}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">{demoUsage.storage.percentage}% used</span>
                <span className="text-xs text-gray-500">{demoUsage.storage.remaining} MB remaining</span>
              </div>
            </div>

            {/* Plan Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Current Plan</span>
                <span className="text-sm text-gray-600">{demoUsage.plan.name} - ₹{demoUsage.plan.price}/month</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-gray-600">No usage data available</p>
      </div>
    );
  }

  // Get status color based on percentage
  const getStatusColor = (percentage) => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 90) return 'bg-red-400';
    if (percentage >= 75) return 'bg-orange-400';
    if (percentage >= 50) return 'bg-yellow-400';
    return 'bg-green-500';
  };

  const getTextColor = (percentage) => {
    if (percentage >= 100) return 'text-red-600';
    if (percentage >= 90) return 'text-red-500';
    if (percentage >= 75) return 'text-orange-500';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {showHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              Current Plan: {usage.plan?.name || 'Unknown'}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              ₹{usage.plan?.price || 0}/month • Usage for {getCurrentMonth()}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Backend auto-tracking enabled • Click refresh to update display
            </p>
          </div>
          <button
            onClick={fetchUsageData}
            className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
            title="Refresh usage data"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      )}

      {/* Operations Usage Card */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Operations</h4>
              <p className="text-sm text-gray-500">Firebase reads, writes & deletes</p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-2xl font-bold ${getTextColor(usage.operations.percentage)}`}>
              {usage.operations.percentage.toFixed(1)}%
            </p>
            <p className="text-xs text-gray-500">used</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-3 overflow-hidden">
          <div
            className={`h-3 ${getStatusColor(usage.operations.percentage)} transition-all duration-500`}
            style={{ width: `${Math.min(usage.operations.percentage, 100)}%` }}
          />
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm">
          <div>
            <span className="font-semibold text-gray-900">
              {usage.operations.current.toLocaleString()}
            </span>
            <span className="text-gray-500"> / {usage.operations.limit.toLocaleString()} operations</span>
          </div>
          <div className="text-gray-600">
            <span className="font-semibold">{usage.operations.remaining.toLocaleString()}</span> remaining
          </div>
        </div>

        {/* Warning/Alert */}
        {usage.operations.exceeded && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.924-1.333-2.732 0L3.732 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-red-800">Limit Exceeded!</p>
                <p className="text-xs text-red-700 mt-1">
                  You've reached your monthly operation limit. Some features may be restricted. Please upgrade your plan to continue.
                </p>
              </div>
            </div>
          </div>
        )}
        {!usage.operations.exceeded && usage.operations.percentage >= 80 && (
          <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-orange-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.924-1.333-2.732 0L3.732 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-orange-800">Approaching Limit</p>
                <p className="text-xs text-orange-700 mt-1">
                  You're using {usage.operations.percentage.toFixed(0)}% of your monthly operations. Consider upgrading to avoid service interruptions.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Storage Usage Card */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Storage</h4>
              <p className="text-sm text-gray-500">Files, images & documents</p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-2xl font-bold ${getTextColor(usage.storage.percentage)}`}>
              {usage.storage.percentage.toFixed(1)}%
            </p>
            <p className="text-xs text-gray-500">used</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-3 overflow-hidden">
          <div
            className={`h-3 ${getStatusColor(usage.storage.percentage)} transition-all duration-500`}
            style={{ width: `${Math.min(usage.storage.percentage, 100)}%` }}
          />
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm">
          <div>
            <span className="font-semibold text-gray-900">
              {usage.storage.current.toFixed(2)} MB
            </span>
            <span className="text-gray-500"> / {usage.storage.limit} MB</span>
          </div>
          <div className="text-gray-600">
            <span className="font-semibold">{usage.storage.remaining.toFixed(2)} MB</span> remaining
          </div>
        </div>

        {/* Warning/Alert */}
        {usage.storage.exceeded && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.924-1.333-2.732 0L3.732 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-red-800">Storage Full!</p>
                <p className="text-xs text-red-700 mt-1">
                  You've reached your storage limit. Delete files or upgrade your plan to upload more.
                </p>
              </div>
            </div>
          </div>
        )}
        {!usage.storage.exceeded && usage.storage.percentage >= 80 && (
          <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-orange-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.924-1.333-2.732 0L3.732 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-orange-800">Storage Almost Full</p>
                <p className="text-xs text-orange-700 mt-1">
                  You're using {usage.storage.percentage.toFixed(0)}% of your storage. Clean up old files or upgrade for more space.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Plan Comparison & Upgrade */}
      {(usage.operations.percentage >= 75 || usage.storage.percentage >= 75) && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            📈 Need More? Upgrade Your Plan
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {Object.entries(PLAN_LIMITS).map(([key, plan]) => (
              <div
                key={key}
                className={`p-4 rounded-lg border-2 ${
                  usage.plan?.currentPlan === key
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="text-center">
                  <h5 className="font-bold text-gray-900">{plan.name}</h5>
                  <p className="text-2xl font-bold text-blue-600 my-2">₹{plan.price}</p>
                  <p className="text-xs text-gray-500 mb-3">per month</p>
                  <div className="space-y-1 text-left text-xs text-gray-600">
                    <p>✓ {plan.operations.toLocaleString()} operations</p>
                    <p>✓ {plan.storage >= 1024 ? `${(plan.storage / 1024).toFixed(0)} GB` : `${plan.storage} MB`} storage</p>
                  </div>
                  {usage.plan?.currentPlan === key && (
                    <span className="inline-block mt-3 px-3 py-1 bg-blue-500 text-white text-xs rounded-full">
                      Current Plan
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => window.location.href = '/upgrades'}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl"
            >
              🚀 Upgrade Plan
            </button>
            <p className="text-xs text-gray-600 mt-2">
              Contact admin to upgrade your company's subscription plan
            </p>
          </div>
        </div>
      )}

      {/* Last Updated */}
      <div className="text-center text-xs text-gray-500">
        Backend tracking enabled • Usage updates automatically
      </div>
    </div>
  );
};

export default UsageMonitor;

